import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isInternalURL, flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { Container as SemanticContainer } from 'semantic-ui-react';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { Redirect } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import config from '@plone/volto/registry';

const LinkView = ({ content }) => {
  const history = useHistory();
  const objectActionsUrl = useSelector((state) => state.actions?.object_url);
  const objectActions = useSelector((state) => state.actions?.object ?? []);

  // Only trust the actions if they belong to the current content item.
  // While navigating, state.actions still holds the previous page's actions,
  // which would incorrectly suppress the redirect until the new @actions response arrives.
  const actionsMatch =
    objectActionsUrl &&
    (content['@id']?.endsWith(objectActionsUrl) ||
      objectActionsUrl === content['@id']);

  const canEdit = actionsMatch
    ? objectActions.some((action) => action.id === 'edit')
    : false; // default to non-editor until actions catch up

  useEffect(() => {
    if (!canEdit) {
      const { remoteUrl } = content;
      if (isInternalURL(remoteUrl)) {
        history.replace(flattenToAppURL(remoteUrl));
      } else if (!__SERVER__) {
        window.location.href = flattenToAppURL(remoteUrl);
      }
    }
  }, [content, history, canEdit]);

  if (__SERVER__ && !canEdit && content.remoteUrl) {
    return <Redirect to={content.remoteUrl} />;
  }

  if (!canEdit) {
    return (
      <div className="link-redirect-spinner">
        <div className="ui active centered inline loader" />
      </div>
    );
  }

  const { title, description, remoteUrl } = content;
  const { openExternalLinkInNewTab } = config.settings;
  const Container =
    config.getComponent({ name: 'Container' }).component || SemanticContainer;

  return (
    <Container id="page-document">
      <h1 className="documentFirstHeading">{title}</h1>
      {content.description && (
        <p className="documentDescription">{description}</p>
      )}
      {remoteUrl && (
        <p>
          <FormattedMessage
            id="The link address is:"
            defaultMessage="The link address is:"
          />{' '}
          <UniversalLink
            href={remoteUrl}
            openLinkInNewTab={
              openExternalLinkInNewTab && !isInternalURL(remoteUrl)
            }
          >
            {flattenToAppURL(remoteUrl)}
          </UniversalLink>
        </p>
      )}
    </Container>
  );
};

LinkView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    remoteUrl: PropTypes.string,
  }),
};

LinkView.defaultProps = {
  content: null,
};

export default LinkView;
